import { useEffect, useState, type ReactNode } from 'react'

import { NavPanel } from './NavPanel'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import { apiGetNotifications } from '../../api/notifications/notificationsApi'
import {
  DASHBOARD_API_BASE_URL,
  USE_DASHBOARD_REAL_BACKEND,
} from '../../api/config/config'
import { ADMIN_TOKEN_KEY } from '../../auth/adminAuth'
type DashboardLayoutProps = {
  activeNavId: string
  title: string
  subtitle: ReactNode
  user: { name: string; email: string; avatar: string }
  /** Gap between the topbar and the page content, in px. Varies per page in the design. */
  contentGap?: number
  showTopbarUser?: boolean
  children: ReactNode
}

type ToastNotification = {
  id: number | string
  title: string
  description: string
  bookingId?: string
}

export const DashboardLayout = ({
  activeNavId,
  title,
  subtitle,
  user,
  contentGap = 25,
  showTopbarUser = true,
  children,
}: DashboardLayoutProps) => {
  const [navOpen, setNavOpen] = useState(false)
  const [navExpanded, setNavExpanded] = useState(false)
  const [toast, setToast] = useState<ToastNotification | null>(null)
  useEffect(() => {
    if (!navOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [navOpen])

  useEffect(() => {
    if (!USE_DASHBOARD_REAL_BACKEND) return

    let stopped = false
    let controller: AbortController | null = null

    const connect = async () => {
      while (!stopped) {
        const token = localStorage.getItem(ADMIN_TOKEN_KEY)

        if (!token) {
          await new Promise((resolve) => window.setTimeout(resolve, 3000))
          continue
        }

        controller = new AbortController()

        try {
          const response = await fetch(
            `${window.location.origin}/api/admin/notifications/stream/`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'text/event-stream',
              },
              cache: 'no-store',
              signal: controller.signal,
            },
          )

          if (!response.ok || !response.body) {
            throw new Error(`SSE connection failed: ${response.status}`)
          }

          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''

          while (!stopped) {
            const { value, done } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            buffer = buffer.replace(/\r\n/g, '\n')

            let boundary = buffer.indexOf('\n\n')

            while (boundary !== -1) {
              const block = buffer.slice(0, boundary)
              buffer = buffer.slice(boundary + 2)

              const eventName =
                block.split('\n').find((line) => line.startsWith('event:'))
                  ?.slice(6).trim() ?? 'message'

              const data = block
                .split('\n')
                .filter((line) => line.startsWith('data:'))
                .map((line) => line.slice(5).trim())
                .join('\n')

              if (eventName === 'notification' && data) {
                try {
                  const payload = JSON.parse(data)
                  const result = await apiGetNotifications()
                  const notification = result.data?.find(
                    (item) => String(item.id) === String(payload.id),
                  )

                  setToast({
                    id: payload.id,
                    title: notification?.title ?? 'New booking received',
                    description:
                      notification?.description ??
                      'A new booking notification has arrived.',
                    bookingId: notification?.booking_reference ?? undefined,
                  })
                } catch (error) {
                  console.error('Failed to load notification:', error)
                }
              }

              boundary = buffer.indexOf('\n\n')
            }
          }

          await reader.cancel().catch(() => {})
        } catch (error) {
          if (
            !stopped &&
            !(error instanceof DOMException && error.name === 'AbortError')
          ) {
            console.error('Notification stream disconnected.')
          }
        }

        if (!stopped) {
          await new Promise((resolve) => window.setTimeout(resolve, 3000))
        }
      }
    }

    void connect()

    return () => {
      stopped = true
      controller?.abort()
    }
  }, [])

  useEffect(() => {
    if (!toast) return

    const timeout = window.setTimeout(() => setToast(null), 10 * 60 * 1000)
    return () => window.clearTimeout(timeout)
  }, [toast])

  return (
    <div className="h-[100dvh] overflow-hidden bg-dash-bg font-host">

{toast && (
  <div
    key={toast.id}
    role="status"
    aria-live="polite"
    className="fixed right-4 top-4 z-[100] w-[calc(100%-2rem)] max-w-[400px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
  >
    <div className="flex items-start gap-3 p-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="21"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M8 2v4" />
          <path d="M16 2v4" />
          <rect width="18" height="18" x="3" y="4" rx="2" />
          <path d="M3 10h18" />
          <path d="m9 16 2 2 4-4" />
        </svg>
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900">
          {toast.title}
        </p>
        <p className="mt-1 text-sm leading-5 text-gray-600">
          {toast.description}
        </p>
      </div>

      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={() => setToast(null)}
        className="shrink-0 rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-900"
      >
        ✕
      </button>
    </div>

    {toast.bookingId && (
      <div className="mx-4 mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
        <p className="text-xs font-medium text-gray-500">
          Booking reference
        </p>
        <p className="mt-1 break-all font-mono text-sm font-semibold text-gray-900">
          {toast.bookingId}
        </p>
      </div>
    )}

    <div className="flex gap-2 border-t border-gray-100 bg-gray-50 p-4">
      {toast.bookingId && (
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(toast.bookingId!)
          }}
          className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-100"
        >
          Copy Booking ID
        </button>
      )}

      <button
        type="button"
        onClick={() => {
          setToast(null)
          window.location.href = '/dashboard/bookings'
        }}
        className="flex-1 rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        View Bookings
      </button>
    </div>
  </div>
)}


      <div className="mx-auto flex h-full w-full max-w-[1409px] items-start px-[16px] py-[20px] sm:px-[24px] lg:py-[37px] xl:px-0">
        <div 
          className="hidden lg:block sticky top-[37px] z-30"
          onMouseLeave={() => setNavExpanded(false)}
        >
          <Sidebar
            activeId={activeNavId}
            expanded={navExpanded}
            onToggle={() => setNavExpanded((open) => !open)}
            onExpand={() => setNavExpanded(true)}
          />
        </div>

        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col lg:pl-[12px]">

  {/* Topbar stays in the normal document flow */}
    {/* Fixed dashboard topbar  outside the scrolling area */}
<div
  className="shrink-0 bg-dash-bg"
  style={{ paddingBottom: contentGap }}
>
  <Topbar
    title={title}
    subtitle={subtitle}
    user={user}
    showUser={showTopbarUser}
    onOpenNav={() => setNavOpen(true)}
  />
</div>

{/* The only main vertical scroll container */}
<div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
  {children}
</div>
</div>
      </div>

      {navOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden" role="dialog" aria-modal aria-label="Navigation">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
            className="absolute inset-0 bg-black/40"
          />
          <div className="relative max-h-full overflow-y-auto bg-dash-bg p-[16px] shadow-dash-rail">
            <NavPanel
              activeId={activeNavId}
              onToggle={() => setNavOpen(false)}
              onSelect={() => setNavOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  )
}
